import { ApiError } from '../lib/api';

/** Milliseconds a toast stays visible before it auto-clears. */
export const TOAST_AUTO_CLEAR_MS = 6000;

/**
 * Prefixes and appends the most helpful message we can extract from a rejected
 * fetcher promise. `ApiError.message` first, `Error.message` second, otherwise
 * a generic string so the toast is never empty.
 */
export function formatErrorToast(prefix: string, error: unknown): string {
  if (error instanceof ApiError) return `${prefix}: ${error.message}`;
  if (error instanceof Error) return `${prefix}: ${error.message}`;
  return `${prefix}: unknown error`;
}

/**
 * True when `error` is an aborted-fetch signal that should be surfaced as a
 * no-op (a newer request cancelled this one) rather than a toast.
 */
export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * Ephemeral, page-wide error banner store. One message at a time — a fresh
 * `show()` replaces the current message and resets the auto-clear timer.
 */
export class ToastStore {
  /** Handle for the auto-clear timer; cleared by `dismiss` and inside `#clearMessage`. */
  #timer: ReturnType<typeof setTimeout> | undefined = undefined;
  /** Currently visible message, or `undefined` when hidden. */
  message = $state<string | undefined>(undefined);

  /** Wipes both the current message and any pending timer, returning to the hidden resting state. */
  #reset(): void {
    if (this.#timer !== undefined) clearTimeout(this.#timer);
    this.#timer = undefined;
    this.message = undefined;
  }

  /** Shows `message`, replacing any prior message and restarting the auto-clear timer. */
  show(message: string): void {
    if (this.#timer !== undefined) clearTimeout(this.#timer);
    this.message = message;
    this.#timer = setTimeout(() => {
      this.#reset();
    }, TOAST_AUTO_CLEAR_MS);
  }

  /** Convenience: extracts a message from `error` via {@link formatErrorToast} and shows it. */
  showError(prefix: string, error: unknown): void {
    if (isAbortError(error)) return;
    this.show(formatErrorToast(prefix, error));
  }

  /** Hides the current message and cancels the auto-clear timer. */
  dismiss(): void {
    this.#reset();
  }
}

/**
 * Page-wide singleton — every silent-swallow site imports this and routes its
 * would-be swallowed errors through `toast.showError(...)`. Tests reset with
 * `toast.dismiss()` in `beforeEach`.
 */
export const toast = new ToastStore();
